/**
 * ContentPlannerWizardPage - Wizard-based content creation flow
 *
 * This is a standalone wizard implementation that can replace ContentPlannerPage
 * or be integrated as an alternative view mode.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ContentPlannerWizard, {
  getWordPressPublishMode,
  shouldExportToGdocs,
} from '@/components/content-planner/ContentPlannerWizard';
import type { WizardState } from '@/components/content-planner/ContentPlannerWizard';
import { useCreateContent } from '@/hooks/useBlogCreationAPI';
import { CreateContentData } from '@/components/BlogCreationCommandCenter/types';
import { useModelStore } from '@/stores/useModelStore';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Must match the private constant in useBlogCreationAPI.ts
const CONTENT_QUERY_KEY = 'blog-creation-content';

const ContentPlannerWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createContentMutation = useCreateContent();
  const queryClient = useQueryClient();
  const generationMode = useModelStore((s) => s.generationMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract URL params
  const templateId = searchParams.get('template');
  const keywordParam = searchParams.get('keyword');
  const entryParam = searchParams.get('entry') as 'have-keywords' | 'find-keywords' | null;
  const defaultStep = entryParam ? 1 : 0;
  const initialStep = searchParams.get('step') ? parseInt(searchParams.get('step')!) : defaultStep;

  // Parse initial keywords from URL
  const initialKeywords = keywordParam ? [keywordParam] : [];

  // Navigate to the content planner only after the content list query is fresh
  const navigateToPlanner = async () => {
    // refetchQueries waits for the fetch to complete — planner mounts with up-to-date data
    await queryClient.refetchQueries({ queryKey: [CONTENT_QUERY_KEY] });
    navigate('/content/planner');
  };

  // Handle wizard completion
  const handleWizardComplete = async (state: WizardState) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const wordpressPublishMode = getWordPressPublishMode(state.publishDestination);
    const publishToWP = wordpressPublishMode === 'publish';
    const exportGdocs = shouldExportToGdocs(state.publishDestination);

    try {
      if (state.entryPoint === 'have-keywords') {
        // Path A: Create single blog post from keywords
        const [primaryKeyword, ...secondaryKeywords] = state.keywords;

        const data: CreateContentData = {
          keyword: primaryKeyword,
          secondary_keywords: secondaryKeywords.length > 0 ? secondaryKeywords : undefined,
          target_word_count: state.targetWordCount,
          template_id: state.templateId || undefined,
          title: state.blogTitle.trim() || undefined,
          publish_immediately: publishToWP || undefined,
          wordpress_publish_mode: wordpressPublishMode,
          export_to_gdocs: exportGdocs || undefined,
          generation_mode: generationMode,
        };

        await createContentMutation.mutateAsync(data);
        await navigateToPlanner();
      } else {
        // Path B: Create multiple blog posts from clusters
        let successCount = 0;
        let failCount = 0;

        for (const cluster of state.selectedClusters) {
          try {
            // Honor the reviewer's per-keyword deselections from the Clusters
            // step (transient excluded_keywords) alongside the always-dropped
            // cluster name.
            const excluded = new Set(cluster.excluded_keywords || []);
            const secondaryKeywords = cluster.keywords_preview?.filter(
              kw => kw !== cluster.name && !excluded.has(kw),
            );
            const payload: CreateContentData = {
              keyword: cluster.name,
              secondary_keywords: secondaryKeywords,
              template_id: state.templateId || undefined,
              source: 'ai',
              target_word_count: state.targetWordCount,
              target_keywords_count: (secondaryKeywords?.length || 0) + 1,
              publish_immediately: publishToWP || undefined,
              wordpress_publish_mode: wordpressPublishMode,
              export_to_gdocs: exportGdocs || undefined,
              generation_mode: generationMode,
            };

            await createContentMutation.mutateAsync(payload);
            successCount++;
          } catch (err) {
            console.error(`Failed to create content for "${cluster.name}":`, err);
            failCount++;
          }
        }

        if (failCount > 0) {
          toast.warning(`${failCount} blog post${failCount > 1 ? 's' : ''} failed to create.`);
        }
        await navigateToPlanner();
      }
    } catch (error) {
      console.error('Failed to create content:', error);
      toast.error('Failed to create content. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle wizard cancellation
  const handleWizardCancel = () => {
    navigate('/content/planner');
  };

  return (
    <div className="h-full overflow-auto p-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/content/planner')}
          className="text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Create Content</h1>
          <p className="text-sm text-muted-foreground">
            Follow the steps to create optimized blog posts
          </p>
        </div>
      </div>

      {/* Wizard */}
      <ContentPlannerWizard
        initialTemplateId={templateId}
        initialKeywords={initialKeywords}
        initialStep={initialStep}
        initialEntryPoint={entryParam}
        isSubmitting={isSubmitting}
        onComplete={handleWizardComplete}
        onCancel={handleWizardCancel}
      />
    </div>
  );
};

export default ContentPlannerWizardPage;
